/**
 * Created by Paul Olle on 08.07.2021
 */
//olympe imports
import {registerBrick, UIBrick, UIContext, CloudObject, Transaction, Color} from 'olympe';
import {getLogger} from "@olympeio/core";
//chartjs imports
import ChartJS from 'chart.js/auto';
//rxjs imports
import {startWith, mergeMap} from "rxjs/operators";
import {combineLatest, from} from 'rxjs';
//local imports
import {ChartsDataSet} from "../../common/deprecated/model/ChartsDataSet";
import {ChartsData} from "../../common/deprecated/model/ChartsData";

/**
 * The purpose of this brick is to provide a generic chart with some placeholder info.
 * You can change the type property to change in real time the layout of the graph using the String Enum type
 * as expected for different types of charts (Pie, Bar, Doughnut, etc)
 */
export default class Chart extends UIBrick {

    /**
     * This method runs when the brick is ready in the HTML DOM.
     * @override
     * @param {!UIContext} context
     * @param {!Element} elementDom
     */
    draw(context, elementDom) {
        this.createPlaceholderDataSet((placeholderDataSet) => {
            //////////////////////////////////////
            //////////// Html structure //////////
            //////////////////////////////////////
            this.chartContainer = document.createElement("div");
            const style = 'position:relative;height:100%;';
            this.chartContainer.setAttribute('style', style);

            // <drawDOM...
            // <div>
            // <canvas></canvas>
            // </div>
            //</drawDOM...

            // Unique identifier without libraries
            this.chartIdentifier = 'chartjsCanvas' + Math.round(Math.random() * Date.now());
            const chartCanvas = this.createACanvas(this.chartContainer, this.chartIdentifier);
            this.chartContainer.onclick = (evt) => {
                const activePoints = this.chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);

                if(activePoints.length > 0){
                    const [{ datasetIndex, index }] = activePoints;
                    let datasets = context.get('dataSets');
                    if(!Array.isArray(datasets)){
                        datasets = [datasets];
                    }

                    const lambda = context.get('On Chart Click');
                    if(lambda !== null){
                        const [ctrlFlow, data] = lambda.getInputs()
                        context.runner(lambda)
                            .set(data, datasets[datasetIndex].getChartsData()[index])
                            .trigger(ctrlFlow);
                    }

                }
            }
            elementDom.appendChild(this.chartContainer);

            ///////////////////////////////////////////////////////////////////
            //////////// Fetch properties and set placeholder values //////////
            ///////////////////////////////////////////////////////////////////
            /////////////////////// chart title property //////////////////////
            const titleProperty = context.getProperty('title');
            const titleObservable = titleProperty
                .observe()
                .pipe(startWith(titleProperty.defaultValue || ''));

            /////////////////////// title fontsize property /////////////////////////////
            const titleFontSizeProperty = context.getProperty('titleFontSize');
            const titleFontSizeObservable = titleFontSizeProperty
                .observe()
                .pipe(startWith(titleFontSizeProperty.defaultValue || 25));

            /////////////////////// global fontsize property /////////////////////////////
            const globalFontSizeProperty = context.getProperty('globalFontSize');
            const globalFontSizeObservable = globalFontSizeProperty
                .observe()
                .pipe(startWith(globalFontSizeProperty.defaultValue || 12));

            /////////////////////// enable tooltips property /////////////////////////////
            const enableToolTipsProperty = context.getProperty('tooltip');
            const enableToolTipsObservable = enableToolTipsProperty
                .observe()
                .pipe(startWith(enableToolTipsProperty.defaultValue || true));

            /////////////////////// maintain aspectRatio property /////////////////////////////
            const maintainAspectRatioProperty = context.getProperty('maintainAspectRatio');
            const maintainAspectRatioObservable = maintainAspectRatioProperty
                .observe()
                .pipe(startWith(maintainAspectRatioProperty.defaultValue || false));

            /////////////////////// invert axis property /////////////////////////////
            const invertAxisProperty = context.getProperty('invertAxis');
            const invertAxisObservable = invertAxisProperty
                .observe()
                .pipe(startWith(invertAxisProperty.defaultValue || false));

            /////////////////////// showMoreDetails property /////////////////////////////
            const showMoreDetailsProperty = context.getProperty('showMoreDetails');
            const showMoreDetailsObservable = showMoreDetailsProperty
                .observe()
                .pipe(startWith(showMoreDetailsProperty.defaultValue || false));

            /////////////////////// time scale property /////////////////////////////
            const timeScaleProperty = context.getProperty('timeScale');
            const timeScaleObservable = timeScaleProperty
                .observe()
                .pipe(startWith(timeScaleProperty.defaultValue || ''));

            //////////////////// 2d context for chartjs /////////////////////////////
            const canvasRenderingContext = chartCanvas.getContext("2d");

            /////////////////////// data property /////////////////////////////
            const dataSetsProperty = context.getProperty('dataSets');
            const dataSetsObservable = dataSetsProperty
                .observe()
                .pipe(startWith(placeholderDataSet));

            /////////////////////// data size property /////////////////////////////
            const dataSizeObservable = dataSetsProperty
                .observe()
                .pipe(mergeMap(dataset => {
                    if(Array.isArray(dataset)){
                        return from(dataset).pipe(mergeMap(ds => ds.getChartsDataList().observeSize()));
                    } else {
                        return dataset.getChartsDataList().observeSize();
                    }
                }));

            ////////////////////////////////////////
            //////////// Draw chart ////////////////
            ////////////////////////////////////////
            combineLatest([
                titleObservable,
                titleFontSizeObservable,
                globalFontSizeObservable,
                enableToolTipsObservable,
                maintainAspectRatioObservable,
                invertAxisObservable,
                timeScaleObservable,
                showMoreDetailsObservable,
                dataSetsObservable,
                dataSizeObservable
            ]).subscribe(([
                  _title,
                  _titleFontSize,
                  _fontSize,
                  _enableToolTips,
                  _maintainAspectRatio,
                  _invertAxis,
                  _timeScale,
                  _showMoreDetails,
                  _dataSets,
                  _dataSizeObservable
                ]) => {
                const logger = getLogger('Chart');
                let noError = true;

                if (typeof _title !== 'string') {
                    logger.error('Title is not a string');
                    noError = false;
                }
                if (typeof _titleFontSize !== 'number') {
                    logger.error('TitleFontSize is not a number');
                    noError = false;
                }
                if (typeof _fontSize !== 'number') {
                    logger.error('FontSize is not a number');
                    noError = false;
                }
                if (typeof _enableToolTips !== 'boolean') {
                    logger.error('EnableToolTips is not a boolean');
                    noError = false;
                }
                if (typeof _maintainAspectRatio !== 'boolean') {
                    logger.error('MaintainAspectRatio is not a boolean');
                    noError = false;
                }
                if (typeof _invertAxis !== 'boolean') {
                    logger.error('InvertAxis is not a boolean');
                    noError = false;
                }
                if (typeof _timeScale !== 'string') {
                    logger.error('TimeScale is not a string');
                    noError = false;
                }
                if (typeof _showMoreDetails !== 'boolean') {
                    logger.error('ShowMoreDetails is not a boolean');
                    noError = false;
                }
                if (!Array.isArray(_dataSets) && !(_dataSets instanceof CloudObject)) {
                    logger.error('DataSets is not an Array or a CloudObject');
                    noError = false;
                }

                if (noError) {
                    const closestTimeScale = this.getMostSimilarEnum(Chart.TIME_SCALES, _timeScale);
                    this.drawChart(
                        canvasRenderingContext,
                        _title,
                        _titleFontSize,
                        _fontSize,
                        _enableToolTips,
                        _maintainAspectRatio,
                        _invertAxis,
                        closestTimeScale,
                        _showMoreDetails,
                        _dataSets
                    );
                }
            });
        });
    }

    ////////////////////////////////////////////////////
    ////////// Chart Manipulation functions ////////////
    ////////////////////////////////////////////////////
    /**
     * Draws the chart by using chartjs
     * @private
     * @param {RenderingContext} initialContext
     * @param {string} title
     * @param {number} titleFontSize
     * @param {number} fontSize
     * @param {boolean} enableToolTips
     * @param {boolean} maintainAspectRatio
     * @param {boolean} invertAxis
     * @param {string} timeScale
     * @param {boolean} showMoreDetails
     * @param {ChartsDataSet | Array<ChartsDataSet>} dataSets
     */
    drawChart(initialContext,
              title,
              titleFontSize,
              fontSize,
              enableToolTips,
              maintainAspectRatio,
              invertAxis,
              timeScale,
              showMoreDetails,
              dataSets
              ) {
        try {
            let context = initialContext;
            if (this.chart) {
                this.chart.destroy();
                this.removeCurrentChart(this.chartIdentifier);
                const newCanvas = this.createACanvas(this.chartContainer, this.chartIdentifier);
                context = newCanvas.getContext("2d");
            }

            //by default, the type is overridden in the dataset but if all datasets have the same type
            // we can set a globaltype (if the user wants more details) so that all Charts get extra layers of details
            // from sharing the same type. However, by doing so, the user can no longer deactivate datasets, that's
            // why he has the option to choose one or another.
            let globalType = undefined;
            if (showMoreDetails) {
                globalType = this.findChartsGlobalType(dataSets);
            }

            //Convert the data contained in Olympe chartsDataSetModel to readable data for ChartJs lib
            const data = this.convertChartsDataSets(dataSets);
            const scales = {};
            if(!this.isEmpty(timeScale)) {
                scales.x = {
                    type: 'time',
                    time: {
                        unit: timeScale
                    }
                }
            }

            // If there is multi axes display them
            if(data.datasets.filter(ds => ds.yAxisID === 'y1').length > 0) {
                scales.y = {
                    type: 'linear',
                    display: true,
                    position: 'left',
                };

                scales.y1 = {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    grid: {
                        drawOnChartArea: false, // only want the grid lines for one axis to show up
                    },
                }
            }
            this.chart = new ChartJS(context, {
                type: globalType,
                data: data,
                options: {
                    maintainAspectRatio: maintainAspectRatio,
                    indexAxis: invertAxis ? 'y' : 'x',
                    scales: scales,
                    plugins : {
                        title: {
                            display: true,
                                text: title,
                                font: {
                                size: titleFontSize
                            }
                        },
                        tooltip: {
                            enabled: enableToolTips
                        }
                    }
                }
            });
            ChartJS.defaults.font.size = fontSize;
        }
        catch (e) {
            console.error(e);
        }
    }

    /**
     * Hard remove the Canvas DOM element to make place for a new one.
     * @private
     * @param {string} id
     */
    removeCurrentChart(id) {
        document.getElementById(id).remove();
    }

    /**
     * Initialises a Canvas ready for use and appends it to the container
     *
     * @private
     * @param {HTMLElement} container
     * @param {string} canvasId
     * @return {HTMLCanvasElement} chartCanvas
     */
    createACanvas(container, canvasId) {
        const chartCanvas = document.createElement("canvas");
        chartCanvas.setAttribute('id', canvasId);
        container.appendChild(chartCanvas);
        return chartCanvas;
    }

    /**
     * Not all Charts support the x:y format, check the documentation for more info :
     * https://www.chartjs.org/docs/latest/general/data-structures.html
     * @private
     * @param {string} type
     * @return {boolean}
     */
    doesChartTypeSupportsCartesian(type) {
        return !(
            type === Chart.CHARTS_TYPES.DOUGHNUT
            || type === Chart.CHARTS_TYPES.PIE
            || type === Chart.CHARTS_TYPES.POLAR
            || type === Chart.CHARTS_TYPES.RADAR
        );
    }

    /**
     * @param {ChartsDataSet | Array<ChartsDataSet>} dataSets
     * @return {string}
     */
    findChartsGlobalType(dataSets) {
        let globalType = undefined;

        if (Array.isArray(dataSets)) {
            for (let i = 0; i < dataSets.length; i++) {
                const type = this.getMostSimilarEnum(Chart.CHARTS_TYPES, dataSets[i].getType(), Chart.CHARTS_TYPES.BAR);
                if (!globalType) {
                    globalType = type; //found a temporary global type possible
                }
                else if(globalType !== type) {
                    return undefined; //no global type possible since datasets have different types
                }
            }
        }
        else {
            // Only one dataSet, his type can be global
            globalType = this.getMostSimilarEnum(Chart.CHARTS_TYPES, dataSets.getType(), Chart.CHARTS_TYPES.BAR);
        }

        //If the chart is Cartesian, global types won't work
        if (this.doesChartTypeSupportsCartesian(globalType)) {
            return undefined;
        }

        return globalType;
    }

    /**
     * Creates a placeholder dataset object for default values
     * @param {function(*)} setData
     */
    createPlaceholderDataSet(setData) {
        const citiesProperties = new Map();
        citiesProperties.set(ChartsDataSet.legendProp, 'No data');
        citiesProperties.set(ChartsDataSet.typeProp, Chart.CHARTS_TYPES.BAR);
        citiesProperties.set(ChartsDataSet.fillProp, false);
        citiesProperties.set(ChartsDataSet.borderWidthProp, 1);
        citiesProperties.set(ChartsDataSet.orderProp, 0);
        citiesProperties.set(ChartsDataSet.borderColorProp, Color.create(55, 55, 55) );

        const newYorkProperties = new Map();
        newYorkProperties.set(ChartsData.labelProp, 'No Data');
        newYorkProperties.set(ChartsData.numberProp, 1000);
        newYorkProperties.set(ChartsData.colorProp, Color.create(128, 128, 128));

        const transaction = new Transaction(false);
        const cities = transaction.create(ChartsDataSet, citiesProperties);
        const newYork = transaction.create(ChartsData, newYorkProperties);
        transaction.createRelation(ChartsDataSet.chartsDataRel, cities, newYork);

        transaction.execute().then(() => {
            setData(CloudObject.get(cities));
        }).catch((message) => {
            getLogger('Placeholder Transaction').error(`An error were encountered while creating a ChartsDataPlaceholder ${message}`);
        });
    }

    /**
     * @param {ChartsDataSet | Array<ChartsDataSet>} dataSets
     * @return {{}}
     */
    convertChartsDataSets(dataSets) {
        ////////////////////////////////////////////
        //////////// Extract functions /////////////
        ////////////////////////////////////////////
        /**
         *
         * @param ChartsDataSet
         * @return {{dataset: {backgroundColor: [], borderColor: string, data: [], borderWidth: number, label: string, fill: boolean}, labels: []}}
         */
        const extractDataFromDataSet = (ChartsDataSet) => {
            const type = this.getMostSimilarEnum(Chart.CHARTS_TYPES, ChartsDataSet.getType(), Chart.CHARTS_TYPES.BAR);
            const newData = {
                labels:[],
                dataset :{
                    type: type,
                    label: ChartsDataSet.getLegend(),
                    data: [],
                    fill: ChartsDataSet.getFill(),
                    backgroundColor: [],
                    order: ChartsDataSet.getOrder(),
                    borderWidth: ChartsDataSet.getBorderWidth(),
                    borderColor: ChartsDataSet.getBorderColor().toHexString(),
                    yAxisID: ChartsDataSet.getSecondAxis() ? 'y1' : 'y'
                }
            };

            const chartsData = ChartsDataSet.getChartsData();
            for (let i = 0; i < chartsData.length; i++) {
                const label = chartsData[i].getLabel();
                const value = chartsData[i].getNumber();
                const color = chartsData[i].getColor();

                // A Cartesian type will override it's label as it's x-axis specified label whereas a non Cartesian
                // will use the more generic labels array. In case of a non cartesian generic usage, positions in the arrays
                // will be matched which means that the first label element will correspond to the first value element.
                if (this.doesChartTypeSupportsCartesian(type)) {
                    newData.dataset.data.push({
                        x: label,
                        y: value
                    });
                }
                else {
                    newData.labels.push(label);
                    newData.dataset.data.push(value)
                }
                newData.dataset.backgroundColor.push(color.toHexString());
            }
            return newData;
        };

        /**
         *
         * @param data
         * @param dataSet
         * @return {{datasets: [], labels: undefined}}
         */
        const fillDataForChartjs = (data, dataSet) => {
            const extractedData = extractDataFromDataSet(dataSet);
            data.datasets.push(extractedData.dataset);
            data.labels = data.labels.concat(extractedData.labels);
            return data;
        };

        /////////////////////////////////
        ////////////  Main  /////////////
        /////////////////////////////////
        const data = {
            labels: [],
            datasets:[]
        };

        if (Array.isArray(dataSets)) {
            for (let i = 0; i < dataSets.length; i++) {
                fillDataForChartjs(data, dataSets[i]);
            }
        }
        else {
            fillDataForChartjs(data, dataSets);
        }

        return data;
    }

    ///////////////////////////////////////
    //////////// Utils functions //////////
    ///////////////////////////////////////
    /**
     * Is a string undefined or has 0 characters
     * @param {string} str
     * @return {boolean}
     */
    isEmpty(str) {
        return (!str || str.length === 0 );
    }

    /**
     * taken from https://stackoverflow.com/questions/10473745/compare-strings-javascript-return-of-likely
     * Checks if two strings are similar and returns a similarity percentage
     * "test","tes"    // 75%
     * "test","test"   // 100%
     * "test","testt"  // 80%
     * "test","tess"   // 75%
     * @param {string} a
     * @param {string} b
     * @return {number} weight between 0 and 1
     */
    similarity(a, b) {
        let equivalency = 0;
        const minLength = (a.length > b.length) ? b.length : a.length;
        const maxLength = (a.length < b.length) ? b.length : a.length;
        for(let i = 0; i < minLength; i++) {
            if(a[i] == b[i]) {
                equivalency++;
            }
        }

        const weight = equivalency / maxLength;
        return weight;
    };

    /**
     * Find the closest similar predefined enum type value to the newly typedWord/subject entered by the User
     * @param {{}} enumObject
     * @param {string} subject The subject string to evaluate
     * @param {string?} defaultEnumValue = '' by default it's empty
     * @param {number?} minimumHighestSimilarity = 0.30 don't even bother with values below this threshold
     */
    getMostSimilarEnum(enumObject, subject, defaultEnumValue = '',  minimumHighestSimilarity = 0.30) {
        let closestEnum = defaultEnumValue;
        Object.values(enumObject).map(possibleEnum => {
            const currentSimilarity = Math.max(this.similarity(possibleEnum, subject), minimumHighestSimilarity);
            if (currentSimilarity > minimumHighestSimilarity) {
                closestEnum = possibleEnum;
                minimumHighestSimilarity = currentSimilarity;
            }
        });
        return closestEnum;
    }
}

/**
 * @enum {string}
 */
Chart.CHARTS_TYPES = {
    LINE : 'line',
    BAR: 'bar',
    RADAR: 'radar',
    PIE: 'pie',
    DOUGHNUT: 'doughnut',
    POLAR: 'polarArea',
    SCATTER: 'scatter'
};

/**
 * @enum {string}
 */
Chart.TIME_SCALES = {
    MILLISECOND: 'millisecond',
    SECOND: 'second',
    MINUTE: 'minute',
    HOUR: 'hour',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    QUARTER: 'quarter',
    YEAR: 'year'
};

registerBrick('017a85d57cfa65ac4ad2', Chart);
